import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Nav } from 'react-bootstrap';
import {
  FaHome,
  FaBullhorn,
  FaGavel,
  FaCalendarAlt,
  FaCalendarCheck,
  FaNewspaper,
  FaUserTie,
  FaFile,
  FaCog,
  FaTree,
  FaBars,
  FaImages,
  FaUserFriends,
  FaProjectDiagram,
  FaUserCircle,
  FaSignOutAlt,
  FaQrcode,
  FaPrint,
  FaBook,
  FaDumbbell,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaHistory
} from 'react-icons/fa';
import { logout } from '../../services/authService';
import './Sidebar.scss';

const MenuItem = ({ icon: Icon, text, to, isActive, onSelect }) => (
  <Nav.Link as={Link} to={to} className={`sidebar-link ${isActive ? 'active' : ''}`} onClick={onSelect}>
    <Icon className="me-2" />
    <span className="sidebar-text">{text}</span>
  </Nav.Link>
);

const Sidebar = ({ isOpen = true, onClose, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (path) => location.pathname === path;

  const user = useMemo(() => {
    const stored = localStorage.getItem('user');
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }, []);

  const role = (user?.userType || user?.role || 'admin').toLowerCase();
  const roleLabel =
    role === 'library'
      ? 'Kütüphane Personeli'
      : role === 'printer'
        ? 'Toner Sorumlusu'
        : role === 'sports' || role === 'sporsuperadmin'
          ? 'Spor Sorumlusu'
          : role === 'regionalsports'
            ? 'Bölge Sorumlusu'
            : role === 'istirakler'
              ? 'İştirak Yetkilisi'
              : role === 'basin'
                ? 'Basın Yayın Merkezi'
                : 'Yönetici';
  const displayName = user?.nameSurname || user?.name || 'Kullanıcı';

  const [qrModeEnabled, setQrModeEnabled] = useState(() => {
    const stored = localStorage.getItem('libraryQrModeEnabled');
    return stored !== 'false';
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('libraryQrModeEnabled');
      setQrModeEnabled(stored !== 'false');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('libraryQrModeChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('libraryQrModeChanged', handleStorageChange);
    };
  }, []);

  const adminMenuItems = [
    { to: '/', text: 'Ana Sayfa', icon: FaHome },
    { to: '/library-appointments', text: 'Kütüphane Randevuları', icon: FaCalendarCheck },
    { to: '/books', text: 'Kitap Yönetimi', icon: FaBook },
    { to: '/sports-appointments', text: 'Spor Randevuları', icon: FaDumbbell },
    { to: '/sports/activity-logs', text: 'Aktivite Kayıtları', icon: FaHistory },
    ...(qrModeEnabled ? [{ to: '/library-appointments/scan', text: 'QR Tara', icon: FaQrcode }] : []),
    { to: '/announcements', text: 'Duyurular', icon: FaBullhorn },
    { to: '/tenders', text: 'İhaleler', icon: FaGavel },
    { to: '/events', text: 'Etkinlikler', icon: FaCalendarAlt },
    { to: '/news', text: 'Haberler', icon: FaNewspaper },
    { to: '/parks', text: 'Parklar', icon: FaTree },
    { to: '/parlament-members', text: 'Meclis Üyeleri', icon: FaUserFriends },
    { to: '/managers', text: 'Müdürlerimiz', icon: FaUserTie },
    { to: '/activity-reports', text: 'Faaliyet Raporları', icon: FaFile },
    { to: '/advertisements', text: 'İlanlar', icon: FaBullhorn },
    { to: '/parlament-decisions', text: 'Meclis Kararları', icon: FaGavel },
    { to: '/projects', text: 'Proje Yönetimi', icon: FaProjectDiagram },
    { to: '/neighborhood-developments', text: 'Mahallem Gelişiyor', icon: FaMapMarkerAlt },
    { to: '/menu', text: 'Menü Yönetimi', icon: FaBars },
    { to: '/sliders', text: 'Slider Yönetimi', icon: FaImages },
    { to: '/printers', text: 'Toner Yönetimi', icon: FaPrint },
    { to: '/settings', text: 'Ayarlar', icon: FaCog }
  ];

  const libraryMenuItems = [
    { to: '/library-appointments', text: 'Kütüphane Randevuları', icon: FaCalendarCheck },
    { to: '/books', text: 'Kitap Yönetimi', icon: FaBook },
    ...(qrModeEnabled ? [{ to: '/library-appointments/scan', text: 'QR Tara', icon: FaQrcode }] : [])
  ];

  const printerMenuItems = [{ to: '/printers', text: 'Toner Yönetimi', icon: FaPrint }];

  const sportsMenuItems = [
    { to: '/sports-appointments', text: 'Spor Randevuları', icon: FaDumbbell },
    { to: '/sports/users', text: 'Kullanıcı Yönetimi', icon: FaUserFriends },
    { to: '/sports/activity-logs', text: 'Aktivite Kayıtları', icon: FaHistory }
  ];

  const regionalSportsMenuItems = [
    { to: '/sports-appointments', text: 'Spor Randevuları', icon: FaDumbbell }
  ];

  const istiraklerMenuItems = [
    { to: '/sports-appointments', text: 'Spor Randevuları', icon: FaDumbbell },
    { to: '/sports/activity-logs', text: 'Aktivite Kayıtları', icon: FaHistory },
    { to: '/club-applications', text: 'Kart Başvuruları', icon: FaFile },
    { to: '/club-payments', text: 'Kulüp Ödemeleri', icon: FaMoneyBillWave }
  ];

  const basinMenuItems = [
    { to: '/projects', text: 'Proje Yönetimi', icon: FaProjectDiagram },
    { to: '/neighborhood-developments', text: 'Mahallem Gelişiyor', icon: FaMapMarkerAlt }
  ];

  const menuItems =
    role === 'library'
      ? libraryMenuItems
      : role === 'printer'
        ? printerMenuItems
        : role === 'sports' || role === 'sporsuperadmin'
          ? sportsMenuItems
          : role === 'regionalsports'
            ? regionalSportsMenuItems
            : role === 'istirakler'
              ? istiraklerMenuItems
              : role === 'basin'
                ? basinMenuItems
                : adminMenuItems;

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const sidebarClasses = ['sidebar'];
  if (isOpen) {
    sidebarClasses.push('is-open');
  }

  const backdropClasses = ['sidebar-backdrop'];
  if (isOpen) {
    backdropClasses.push('show');
  }

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          className="sidebar-open-btn"
          onClick={onToggle}
          aria-label="Menüyü aç"
          title="Menüyü aç"
        >
          <FaBars />
        </button>
      )}
      <aside className={sidebarClasses.join(' ')}>
        <div className="sidebar-header">
          <img src="/logo.png" alt="Logo" className="sidebar-logo" />
          {isOpen && (
            <button
              type="button"
              className="sidebar-close-btn"
              onClick={onClose}
              aria-label="Menüyü kapat"
              title="Menüyü kapat"
            >
              <FaBars />
            </button>
          )}
        </div>
        <Nav className="flex-column">
          {menuItems.map((item, index) => (
            <MenuItem
              key={index}
              icon={item.icon}
              text={item.text}
              to={item.to}
              isActive={isActive(item.to)}
              onSelect={() => {
                if (typeof window !== 'undefined' && window.innerWidth < 992) {
                  onClose?.();
                }
              }}
            />
          ))}
        </Nav>
        <div className="sidebar-footer">
          <div className="sidebar-profile">
            <FaUserCircle className="sidebar-profile-icon" />
            <div className="sidebar-profile-info">
              <span className="sidebar-profile-name">{displayName}</span>
              <span className="sidebar-profile-role">{roleLabel}</span>
            </div>
          </div>
          <button type="button" className="sidebar-logout" onClick={handleLogout}>
            <FaSignOutAlt />
            Çıkış Yap
          </button>
        </div>
      </aside>
      <button
        type="button"
        className={backdropClasses.join(' ')}
        onClick={onClose}
        aria-label="Menüyü kapat"
        tabIndex={isOpen ? 0 : -1}
        aria-hidden={isOpen ? 'false' : 'true'}
      />
    </>
  );
};

export default Sidebar;
